//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region Asset Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

import { Api, LocalDictionaryFactory } from './../api/api.module'
import { ColumnModel } from '../api/api.models'

import { OpRoute } from '../../models/core'
import { Activity, Enums } from '../../models/common'

import { ConfigHelper, EventsNames, LocalizationHelper, TemplateHelper } from '../../helpers'
import { CommonHelper } from '../../helpers/common-helper'

import { RouteService } from '../../services/route'
import { SystemService } from '../../services/system'
import { UserSessionService } from '../../services/user-session'

import { IMRConfirmationPopupOptions, IIMRConfirmationPopup, IMRConfirmationPopupInitializedArgs } from '../common/confirmation-popup2'
import { GridTypes, GridController, GridButtonsOptions } from '../common/grid'
import { BaseGridController, ScopeBaseGrid } from '../common/common.base.grid.controller'
import { ImportOptions } from '../common/import'
import { ExportOptions } from '../common/export'


export class RoutesController extends BaseGridController {

    //#region Consts

    private get ROUTE_GRID_NAME() { return 'RouteGrid' }

    //#endregion


    //#region Members

    public confirmationPopupOptions: IMRConfirmationPopupOptions

    private confirmationPopup: IIMRConfirmationPopup
    private grid: GridController<OpRoute>

    //#endregion


    //#region Ctor

    constructor(
        protected $scope: ScopeBaseGrid,
        $state: StateService,
        $timeout: ng.ITimeoutService,
        api: Api,
        localDictionary: LocalDictionaryFactory,
        protected userSessionService: UserSessionService,
        private routeService: RouteService,
        protected systemService: SystemService,
        referenctType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSessionService, systemService, api, localDictionary, null, referenctType)

        this.initializeConfirmationPopup()
        this.setEvents()
        this.setMainGridOptions()
    }

    //#endregion


    //#region Events 

    public onAdd(): void {
        this.systemService.openCreateEdit(this.routeService.getCreateEditOptions())
    }

    private onConfirmDelete(): void {
        this.routeService
            .deleteRoute(this.grid.selectedItemId)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
            })
    }

    private onDelete(): void {
        this.confirmationPopup.show()
    }

    private onDeleted(): void {
        this.grid.dxGrid.refresh()
    }

    public onEdit(): void {
        if (_.size(this.dataGrid.getSelectedRowKeys()) === 1) {
            this.systemService.openCreateEdit(this.routeService.getCreateEditOptions(this.grid.selectedItemId))
        }
        else {
            const columnCount: number = this.dataGrid.columnCount()
            const columns: any[] = []

            for (let i = 0; i < columnCount; i++) {
                columns.push(this.dataGrid.columnOption(i));
            }

            const gridData = angular.copy(this.dataGrid.getSelectedRowsData())

            this.systemService.openBulkCreateEdit(this.routeService.getBulkCreateEditOptions(gridData, columns))
        }
    }

    private onInitialized(e: any, grid: GridController<OpRoute>): void {
        this.grid = grid
        this.dataGrid = e.component

        this.setGridId(e.tableId)
    }

    private onShowDetails(e: any): void {
        this.systemService.openDetails(this.routeService.getDetailsOptions(this.grid.selectedItemId, this.grid.pageItemsIds))
    }

    //#endregion


    //#region Confirmation Popup

    private initializeConfirmationPopup(): void {
        this.confirmationPopupOptions = {
            content: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => {
                this.onConfirmDelete()
            },

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            }
        }
    }

    //#endregion


    //#region Setup

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items: any[] = []

        if (this.userSession.getLoggedOperator().hasPermission(Activity.ROUTE_EDIT)) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit), ConfigHelper.Icon.EDIT),

                onItemClick: (e: any) => this.onEdit(),
            })
        }

        return this.mergeWithDefaultItems(isTouchScreen, items);
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onDeleted()
        )

        this.$scope.showDetails = (e: any) => this.onShowDetails(e)
    }

    private getGridButtonsOptions(): GridButtonsOptions {
        return {
            enabled: true,

            addPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ROUTE_ADD) ? "['" + Activity[Activity.ROUTE_ADD] + "']" : null,
            deletePermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ROUTE_DEL) ? "['" + Activity[Activity.ROUTE_DEL] + "']" : null,
            editPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ROUTE_EDIT) ? "['" + Activity[Activity.ROUTE_EDIT] + "']" : null,
            exportPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ROUTE_EXPORT_OP) ? "['" + Activity[Activity.ROUTE_EXPORT_OP] + "']" : null,
            importPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ROUTE_IMPORT_OP) ? "['" + Activity[Activity.ROUTE_IMPORT_OP] + "']" : null,
            saveColumnSettingsPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            showDetailsPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ROUTE_LIST) ? "['" + Activity[Activity.ROUTE_LIST] + "']" : null,

            onAddClick: () => this.onAdd(),
            onDeleteClick: () => this.onDelete(),
            onEditClick: () => this.onEdit(),
            onShowClick: (e: any) => this.onShowDetails(e),
        }
    }

    private getExportOptions(): ExportOptions {
        return {
            referenceType: this.referenceType,
            pluginOptions: {
                moduleName: this.moduleName,
                popupName: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__EXPORT, this.moduleName),
            },
        }
    }

    private getImportOptions(): ImportOptions {
        return {
            dictionaryMethodName: null,
            popupName: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ImportRoutes),
            requestMethodType: null,
            referenceType: this.referenceType,
            moduleName: this.moduleName,
            columnsReorderingDisabled: this.userSessionService.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.RouteModuleMainGrid)
        }
    }

    private setMainGridOptions(): void {
        this.routeService
            .getMainGridColumns()
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    referenceId: this.referenceType,
                    idReferenceType: this.referenceType,
                    type: GridTypes.MAIN,
                    name: this.ROUTE_GRID_NAME,
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    columnsDictionary: columns,
                    dataSourceAdditionalFields: null,
                    defaultColumnsDictionary: defaultColumns,
                    importOptions: this.getImportOptions(),
                    exportOptions: this.getExportOptions(),
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.routeSaved,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,

                    allowColumnReordering: !this.userSessionService
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.RouteModuleMainGrid),

                    getDataSourceMethod: this.userSession
                        .getDataProvider()
                        .Route.getRouteFilter,

                    onInitialized: (e: any, grid: GridController<OpRoute>) => this.onInitialized(e, grid),
                    onDataReady: (data: any) => this.onDataReady(data),
                    onRowDoubleClick: (e: any) => this.onShowDetails(e),
                    contextMenuItems: () => this.getContextMenuItems(),
                }
            })
    }

    //#endregion
}

